import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { MultiSelect, MultiSelectChangeEvent } from '@progress/kendo-react-dropdowns';
import { Loader } from '@progress/kendo-react-indicators';
import { TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import React, { useCallback, useMemo } from 'react';
import { useMultiWindowContext } from '../../../app/components/multi-window/context/multi-window-context';
import { useEvent } from '../../../app/contexts/event-bus';
import { useAppSelector } from '../../../app/hooks/store-hooks';
import useIntegration from '../../../hooks/use-integration';
import { RouteRequestType } from '../../../services/actor-messages';
import { FpmRequestKind, FpmResultSeverity, requestFpmRoutes } from '../../../services/integration/fpm-request-tracker';
import { DrawingObject, DrawingObjectCategory } from '../../drawing-object/types';

export interface ImportRoutesProps {
    onClose: () => void;
}

export function FpmImportRoutes(props: { onClose: () => void }) {
    const multiWindowContext = useMultiWindowContext();
    const [origin, setOrigin] = React.useState('');
    const [destination, setDestination] = React.useState('');
    const [selectedAvoidanceAreas, setSelectedAvoidanceAreas] = React.useState<DrawingObject[]>([]);
    const [result, setResult] = React.useState<{ severity: FpmResultSeverity; message: string } | null>(null);
    const [busy, setBusy] = React.useState(false);
    const integrationInfo = useIntegration();
    const objects = useAppSelector((x) => x.drawingObjects.objects);
    const avoidanceAreaObjects = useMemo(() => objects.filter((x) => x.category === DrawingObjectCategory.avoidance), [objects]);
    const handleOriginChanged = React.useCallback((event: TextBoxChangeEvent) => setOrigin(event.value as string), []);
    const handleDestinationChanged = React.useCallback((event: TextBoxChangeEvent) => setDestination(event.value as string), []);
    const importEnabled = origin && destination && !busy;

    const handleImport = async () => {
        setResult(null);
        setBusy(true);

        await requestFpmRoutes({
            acid: null,
            origin: origin,
            destination: destination,
            edt: null,
            type: RouteRequestType.Routes,
            avoidanceAreas: selectedAvoidanceAreas
        });
    };

    useEvent(
        'fpm-request-finished',
        useCallback(
            (event) => {
                if (event.kind !== FpmRequestKind.routes) {
                    return;
                }
                if (event.severity === 'success') {
                    props.onClose();
                    return;
                }
                setBusy(false);
                setResult({ severity: event.severity, message: event.message });
            },
            [props.onClose]
        )
    );

    return (
        <Dialog
            id="route-analyzer-fpm-import"
            width={400}
            title={`Import from ${integrationInfo.typeText}`}
            onClose={props.onClose}
            appendTo={multiWindowContext.root}>
            <div className="d-flex gap-2 flex-column pb-2">
                <TextBox className="input-uppercase" placeholder="Origin" value={origin} onChange={handleOriginChanged}></TextBox>
                <TextBox
                    className="input-uppercase"
                    placeholder="Destination"
                    value={destination}
                    onChange={handleDestinationChanged}></TextBox>
                <div>Avoidance Areas (optional)</div>
                <MultiSelect
                    data={avoidanceAreaObjects}
                    onChange={(event: MultiSelectChangeEvent) => {
                        const selectedObjects = event.value as DrawingObject[];
                        setSelectedAvoidanceAreas([...selectedObjects]);
                    }}
                    dataItemKey="id"
                    textField="name"
                    value={selectedAvoidanceAreas}
                    placeholder="Please select ..."
                />
                {result && <div className={result.severity === 'error' ? 'text-danger' : 'text-warning'}>{result.message}</div>}
                {busy && <Loader type="infinite-spinner" className="mx-auto" />}
            </div>
            <DialogActionsBar>
                <div className="d-flex gap-2">
                    <Button
                        className="flex-grow-1"
                        themeColor={'primary'}
                        onClick={handleImport}
                        disabled={!importEnabled}
                        id="route-analyzer-database-import-btn">
                        Import
                    </Button>
                    <Button className="flex-grow-1" fillMode={'outline'} onClick={props.onClose}>
                        Cancel
                    </Button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
}
